import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Select,
  DatePicker,
  Input,
  InputNumber,
  Row,
  Col,
  Typography,
  Collapse,
  Alert,
} from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import api from '@/services/api.service';
import directPurchaseInvoiceService from '@/services/directPurchaseInvoice.service';
import { useCurrency } from '@/contexts/CurrencyContext';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;

const INVOICE_STATUSES = ['Draft', 'Submitted', 'Approved', 'Posted', 'Cancelled'] as const;
const PAYMENT_STATUSES = ['Unpaid', 'Partially Paid', 'Paid'] as const;

function invoiceStatusLabel(status: string, t: (key: string) => string): string {
  const map: Record<string, string> = {
    Draft: t('statusDraft'),
    Submitted: t('statusSubmitted'),
    Approved: t('statusApproved'),
    Posted: t('statusPosted'),
    Cancelled: t('statusCancelled'),
  };
  return map[status] ?? status;
}

function paymentStatusLabel(status: string, t: (key: string) => string): string {
  const map: Record<string, string> = {
    Unpaid: t('statusUnpaid'),
    'Partially Paid': t('statusPartiallyPaid'),
    Paid: t('statusPaid'),
  };
  return map[status] ?? status;
}

const DirectPurchaseInvoicesPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();

  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [status, setStatus] = useState<string>('');
  const [paymentStatus, setPaymentStatus] = useState<string>('');
  const [supplierId, setSupplierId] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [amountMin, setAmountMin] = useState<number | null>(null);
  const [amountMax, setAmountMax] = useState<number | null>(null);
  const [department, setDepartment] = useState('');
  const [costCenter, setCostCenter] = useState('');

  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers-list-dpi-filter'],
    queryFn: async () => {
      const res = await api.get<unknown>('/procurement/suppliers');
      const data = (res as { data?: unknown })?.data ?? res;
      const list = Array.isArray((data as { data?: unknown[] })?.data)
        ? (data as { data: unknown[] }).data
        : Array.isArray(data)
          ? data
          : [];
      return list as Array<{ id: string; supplier_name?: string; supplier_code?: string }>;
    },
    staleTime: 60_000,
  });

  const supplierOptions = useMemo(
    () =>
      suppliers.map((s) => ({
        value: s.id,
        label: `${s.supplier_name ?? ''}${s.supplier_code ? ` (${s.supplier_code})` : ''}`,
      })),
    [suppliers]
  );

  const filters = useMemo(
    () => ({
      bill_number: searchText.trim() || undefined,
      status: status || undefined,
      payment_status: paymentStatus || undefined,
      supplier_id: supplierId || undefined,
      date_from: dateRange[0]?.format('YYYY-MM-DD'),
      date_to: dateRange[1]?.format('YYYY-MM-DD'),
      amount_min: amountMin != null ? amountMin : undefined,
      amount_max: amountMax != null ? amountMax : undefined,
      department: department.trim() || undefined,
      cost_center: costCenter.trim() || undefined,
    }),
    [searchText, status, paymentStatus, supplierId, dateRange, amountMin, amountMax, department, costCenter]
  );

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['direct-purchase-invoices', filters],
    queryFn: () => directPurchaseInvoiceService.list(filters),
  });

  const applySearch = (value?: string) => {
    setSearchText((value ?? searchInput).trim());
  };

  const clearAdvanced = () => {
    setStatus('');
    setPaymentStatus('');
    setSupplierId(undefined);
    setDateRange([null, null]);
    setAmountMin(null);
    setAmountMax(null);
    setDepartment('');
    setCostCenter('');
  };

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            {t('directPurchaseInvoices')}
          </Title>
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12, maxWidth: 720 }}
            message={t('dpiRegister')}
            description={t('dpiSearchHint')}
          />
        </Col>
        <Col>
          <Space>
            <Button onClick={() => navigate('/fin/reports/direct-purchase-invoice-register')}>{t('registerReport')}</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/fin/direct-purchase-invoices/new')}>
              {t('newDirectInvoice')}
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
          <Search
            placeholder={t('searchDpiPlaceholder')}
            allowClear
            enterButton={<SearchOutlined />}
            value={searchInput}
            onChange={(e) => {
              const v = e.target.value;
              setSearchInput(v);
              if (!v.trim()) setSearchText('');
            }}
            onSearch={applySearch}
            style={{ maxWidth: 720 }}
          />
          <Collapse
            ghost
            items={[
              {
                key: 'advanced',
                label: <Text type="secondary">{t('advancedFilters')}</Text>,
                children: (
                  <Space wrap size="middle" align="end">
                    <Space direction="vertical" size={4}>
                      <Text type="secondary">{t('supplier')}</Text>
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder={t('allSuppliers')}
                        value={supplierId}
                        onChange={setSupplierId}
                        options={supplierOptions}
                        style={{ width: 280 }}
                      />
                    </Space>
                    <Space direction="vertical" size={4}>
                      <Text type="secondary">{t('invoiceStatus')}</Text>
                      <Select
                        allowClear
                        placeholder={t('any')}
                        style={{ width: 140 }}
                        value={status || undefined}
                        onChange={(v) => setStatus(v ?? '')}
                        options={INVOICE_STATUSES.map((s) => ({ value: s, label: invoiceStatusLabel(s, t) }))}
                      />
                    </Space>
                    <Space direction="vertical" size={4}>
                      <Text type="secondary">{t('paymentStatus')}</Text>
                      <Select
                        allowClear
                        placeholder={t('any')}
                        style={{ width: 150 }}
                        value={paymentStatus || undefined}
                        onChange={(v) => setPaymentStatus(v ?? '')}
                        options={PAYMENT_STATUSES.map((s) => ({ value: s, label: paymentStatusLabel(s, t) }))}
                      />
                    </Space>
                    <Space direction="vertical" size={4}>
                      <Text type="secondary">{t('invoiceDate')}</Text>
                      <RangePicker
                        style={{ width: 260 }}
                        value={dateRange as [dayjs.Dayjs | null, dayjs.Dayjs | null]}
                        onChange={(d) => setDateRange(d ?? [null, null])}
                      />
                    </Space>
                    <Space direction="vertical" size={4}>
                      <Text type="secondary">{t('amountFrom')}</Text>
                      <InputNumber
                        min={0}
                        style={{ width: 120 }}
                        placeholder={t('min')}
                        value={amountMin}
                        onChange={(v) => setAmountMin(typeof v === 'number' ? v : null)}
                      />
                    </Space>
                    <Space direction="vertical" size={4}>
                      <Text type="secondary">{t('amountTo')}</Text>
                      <InputNumber
                        min={0}
                        style={{ width: 120 }}
                        placeholder={t('max')}
                        value={amountMax}
                        onChange={(v) => setAmountMax(typeof v === 'number' ? v : null)}
                      />
                    </Space>
                    <Space direction="vertical" size={4}>
                      <Text type="secondary">{t('department')}</Text>
                      <Input
                        allowClear
                        placeholder={t('department')}
                        style={{ width: 140 }}
                        value={department}
                        onChange={(e) => setDepartment(e.target.value)}
                      />
                    </Space>
                    <Space direction="vertical" size={4}>
                      <Text type="secondary">{t('costCenter')}</Text>
                      <Input
                        allowClear
                        placeholder={t('costCenter')}
                        style={{ width: 140 }}
                        value={costCenter}
                        onChange={(e) => setCostCenter(e.target.value)}
                      />
                    </Space>
                    <Button onClick={clearAdvanced}>{t('clearFilters')}</Button>
                  </Space>
                ),
              },
            ]}
          />
        </Space>

        <Table
          loading={isLoading}
          rowKey="id"
          dataSource={list}
          size="small"
          sticky={{ offsetHeader: 0 }}
          scroll={{ x: 1200, y: 520 }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => t('totalInvoices', { count: total }),
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          columns={[
            {
              title: t('dpiNo'),
              dataIndex: 'invoice_number',
              fixed: 'left',
              width: 140,
              render: (v, r) => <a onClick={() => navigate(`/fin/direct-purchase-invoices/${r.id}`)}>{v}</a>,
            },
            { title: t('supplierInvShort'), dataIndex: 'supplier_invoice_number', width: 140 },
            {
              title: t('supplier'),
              key: 'sup',
              width: 200,
              render: (_, r) => r.supplier?.supplier_name ?? '—',
            },
            {
              title: t('date'),
              dataIndex: 'supplier_invoice_date',
              width: 110,
              render: (d) => (d ? dayjs(d).format('DD MMM YYYY') : '—'),
            },
            {
              title: t('gross'),
              dataIndex: 'grand_total',
              align: 'right',
              width: 120,
              render: (v) => formatCurrency(Number(v)),
            },
            {
              title: t('outstanding'),
              dataIndex: 'balance_due',
              align: 'right',
              width: 120,
              render: (v) => formatCurrency(Number(v)),
            },
            {
              title: t('status'),
              dataIndex: 'invoice_status',
              width: 110,
              render: (s) => <Tag>{invoiceStatusLabel(s, t)}</Tag>,
            },
            {
              title: t('payment'),
              dataIndex: 'payment_status',
              width: 120,
              render: (s) => <Tag>{paymentStatusLabel(s, t)}</Tag>,
            },
            {
              title: '',
              key: 'act',
              width: 90,
              render: (_, r) => (
                <Space>
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`/fin/direct-purchase-invoices/${r.id}`)}
                  />
                  {['Draft', 'Submitted'].includes(r.invoice_status) && (
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => navigate(`/fin/direct-purchase-invoices/${r.id}/edit`)}
                    />
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default DirectPurchaseInvoicesPage;
